import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/ToastContainer";
import { parseApiError } from "../utils/errorHandler";
import pmLogo from "../assets/logo.png";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!email) {
      setError("Please enter your email address");
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.forgotPassword(email);

      if (response && response.status === "success") {
        setIsSubmitted(true);
        showSuccess("Password reset email sent!", "Check your inbox");
      } else {
        const msg = "Unexpected response from server";
        setError(msg);
        showError(msg, "Request Failed");
      }
    } catch (err: any) {
      console.error("Forgot password error:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Request Failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo Section */}
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 p-3">
            <img
              src={pmLogo}
              alt="AH Planning"
              className="w-full h-full object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-[#102040] tracking-tight">
            Forgot Password?
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            No worries, we'll send you reset instructions
          </p>
        </div>

        <Card className="p-8 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-[#102040] mb-2">
                Check your email
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                We've sent a password reset link to{" "}
                <span className="font-medium text-gray-900">{email}</span>
              </p>
              <p className="text-xs text-gray-500 mb-6">
                The link expires in 30 minutes. If you don't receive the email,
                check your spam folder.
              </p>
              <Button
                variant="primary"
                onClick={handleBackToLogin}
                className="w-full"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start gap-3">
                  <div className="w-5 h-5 text-red-500 shrink-0 mt-0.5 flex items-center justify-center">
                    !
                  </div>
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError(null);
                      }}
                      placeholder="admin@pm-gate.net"
                      className="pl-10"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full h-11 text-base font-medium shadow-md shadow-[#f09040]/20 hover:shadow-[#f09040]/40 transition-all"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#f09040] transition-colors"
                  disabled={isLoading}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </Card>

        <p className="text-center text-sm text-gray-500">
          © {new Date().getFullYear()} AH Planning. All rights reserved.
        </p>
      </div>
    </div>
  );
}
